import { makeAutoObservable } from "mobx";
import { ZSpecCommon } from "src/business/specification";
import {
  GroupChecked,
  groupCheckedExt,
  nextCheck,
} from "src/components/ReactiveTable/utils/groupChecked";
import { ZOption } from "src/types";

import { AttrVisitor } from "../../AttrVisitor";
import { walkAttrsInSpecs } from "../../AttrVisitor/walkAttr";
import { AttrDetails, buildAttrsList } from "./buildAttrsList";
import { buildAttrTransColumns } from "./buildAttrTransColumns";

export class AttrTransferPartStore {
  constructor(
    public specs: ZSpecCommon[],
    public isAttrVisible: (uid: string) => boolean,
  ) {
    makeAutoObservable(this);
    this.buildList();
  }

  attrList: AttrDetails[] = [];

  buildList() {
    this.attrList = buildAttrsList(this.specs, this.isAttrVisible);
    this.selected = {};
  }

  get columns() {
    return buildAttrTransColumns(this);
  }

  get rows(): AttrDetails[] {
    let list = this.attrList.filter(
      ({ ownerIds }) => !ownerIds.find((id) => this.collapsed.has(id)),
    );
    // Фильтр по спецификации
    const { visibleSpecs } = this;
    if (visibleSpecs.size > 0) {
      list = list.filter(({ spec }) => visibleSpecs.has(spec.id));
    }
    // Фильтр по части названия атрибута
    const search = this.search.toLowerCase();
    if (search) {
      const isMatch = (name: string): boolean =>
        name.toLowerCase().includes(search);
      list = list.filter(({ attr }) => isMatch(attr.name));
    }
    return list;
  }

  search = "";

  setSearch(newValue: string) {
    this.search = newValue;
  }

  get visibleSpecs(): Set<string> {
    return new Set(this.visibleSpecIds);
  }

  visibleSpecIds: string[] = [];

  setVisibleSpecIds(ids: string[]) {
    this.visibleSpecIds = ids;
  }

  collapsed = new Set<string>();

  toggleCollapse(uid: string) {
    const { collapsed } = this;
    if (collapsed.has(uid)) {
      collapsed.delete(uid);
    } else {
      collapsed.add(uid);
    }
  }

  get specOptions(): ZOption[] {
    return this.specs.map(({ id, name }) => ({ value: id, label: name }));
  }

  selected: Record<string, GroupChecked> = {};

  toggleSelection(attrId: string) {
    const { selected, isAttrVisible } = this;
    let ownerState: GroupChecked | undefined;
    const visitor: AttrVisitor = {
      isSkip({ uid }) {
        return !isAttrVisible(uid);
      },
      onEach({ uid }) {
        if (ownerState) {
          selected[uid] = ownerState;
        }
      },
      onLeaf({ uid }) {
        if (uid === attrId) {
          selected[uid] = nextCheck(selected[uid] ?? "none");
        }
      },
      onEnter({ uid }) {
        if (uid === attrId) {
          ownerState = nextCheck(selected[uid] ?? "none");
          selected[uid] = ownerState;
        }
      },
      onLeave({ uid }, { fields }) {
        if (uid === attrId) {
          ownerState = undefined;
        } else {
          const visibleFields = (fields ?? []).filter((field) =>
            isAttrVisible(field.uid),
          );
          selected[uid] =
            groupCheckedExt(
              visibleFields,
              (field) => selected[field.uid] ?? "none",
            ) ?? "none";
        }
      },
    };
    walkAttrsInSpecs(this.specs, visitor);
  }

  get topSelected(): GroupChecked {
    const { selected } = this;
    return (
      groupCheckedExt(
        this.attrList,
        ({ attr }) => selected[attr.uid] ?? "none",
      ) ?? "none"
    );
  }

  toggleTopSelection() {
    const { selected, attrList, topSelected } = this;
    const value = nextCheck(topSelected);
    attrList.forEach(({ attr }) => {
      selected[attr.uid] = value;
    });
  }

  // Список выбранных строк (полностью или частично). Независимо от того, свёрнуты они или нет.
  get selectedRows(): AttrDetails[] {
    const { selected } = this;
    return this.attrList.filter(({ attr }) => {
      const v = selected[attr.uid];
      return v === "all" || v === "partial";
    });
  }
}
